// Přihlašovací obrazovka: vlevo vínový panel s větrem a logem (brandovatelné zákazníkem), vpravo formulář.
// Malé logo WANET s odkazem + název a verze systému zůstávají vždy.
import { api, ApiError, setToken } from '../api';
import type { PageBoot, UserInfo } from '../contract';
import { h, svg } from '../dom';
import { t, tk } from '../i18n';
import { WANET_LOGO, IWAN_MARK } from '../icons';
import { startWind } from './wind';

export interface LoginResult {
  token: string;
  user: UserInfo;
}

const REMEMBERED_KEY = 'iwan.remembered';

interface Remembered {
  login: string;
  name: string;
}

function remembered(): Remembered[] {
  try {
    const v = JSON.parse(localStorage.getItem(REMEMBERED_KEY) ?? '[]');
    return Array.isArray(v) ? (v as Remembered[]).slice(0, 5) : [];
  } catch {
    return [];
  }
}

export function rememberAccount(user: UserInfo): void {
  try {
    const list = remembered().filter((r) => r.login !== user.login);
    list.unshift({ login: user.login, name: user.name });
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify(list.slice(0, 5)));
  } catch {
    /* ignore */
  }
}

export function forgetAccount(login: string): void {
  try {
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify(remembered().filter((r) => r.login !== login)));
  } catch {
    /* ignore */
  }
}

export function loginScreen(boot: PageBoot, container: HTMLElement, options: { presetLogin?: string; message?: string; switching?: boolean } = {}): Promise<LoginResult> {
  return new Promise((resolve) => {
    const branding = boot.branding;
    const brandName = typeof branding['name'] === 'string' && branding['name'] ? branding['name'] : boot.customer.name;
    const tagline = typeof branding['tagline'] === 'string' ? branding['tagline'] : t('core.ui.lead');
    const logoUrl = typeof branding['logo'] === 'string' ? branding['logo'] : null;

    const loginInput = h('input', { class: 'input', id: 'login', name: 'login', type: 'text', autocomplete: 'username', required: true, autofocus: true, value: options.presetLogin ?? '' });
    const pwdInput = h('input', { class: 'input', id: 'password', name: 'password', type: 'password', autocomplete: 'current-password', required: true });
    const remember = h('input', { type: 'checkbox', id: 'remember', name: 'remember' });
    const codeInput = h('input', { class: 'input', id: 'otp', name: 'otp', type: 'text', inputmode: 'numeric', autocomplete: 'one-time-code', placeholder: '123 456', maxlength: '9' });
    const codeField = h('div', { class: 'field', hidden: true }, h('label', { class: 'field-label', for: 'otp' }, t('core.login.code')), codeInput, h('div', { class: 'field-help' }, t('core.login.code_help')));
    const flash = h('div', { class: 'flash', hidden: !options.message }, options.message ?? '');
    const submit = h('button', { class: 'btn primary wide', type: 'submit' }, t('core.ui.sign_in'));

    const accounts = remembered();
    const tiles = accounts.length && !options.presetLogin
      ? h(
          'div',
          { class: 'account-tiles' },
          accounts.map((a) =>
            h(
              'button',
              { type: 'button', class: 'account-tile', onClick: () => {
                loginInput.value = a.login;
                tiles?.remove();
                form.classList.remove('hidden-form');
                pwdInput.focus();
              } },
              h('span', { class: 'avatar' }, a.name.split(' ').map((p) => p[0] ?? '').join('').slice(0, 2).toUpperCase()),
              h('span', { class: 'tile-name' }, a.name),
              h('span', { class: 'tile-login' }, a.login),
            ),
          ),
          h('button', { type: 'button', class: 'btn ghost wide', onClick: () => {
            tiles?.remove();
            form.classList.remove('hidden-form');
            loginInput.focus();
          } }, t('core.ui.other_account')),
        )
      : null;

    const form = h(
      'form',
      { class: 'login-fields' + (tiles ? ' hidden-form' : ''), method: 'post', autocomplete: 'on' },
      flash,
      h('div', { class: 'field' }, h('label', { class: 'field-label', for: 'login' }, t('core.ui.login')), loginInput),
      h('div', { class: 'field' }, h('label', { class: 'field-label', for: 'password' }, t('core.ui.password')), pwdInput),
      codeField,
      h('label', { class: 'check inline' }, remember, h('span', null, t('core.ui.remember'))),
      submit,
      h('p', { class: 'foot muted' }, t('core.ui.forgot')),
    );

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      submit.disabled = true;
      submit.textContent = t('core.ui.signing_in');
      flash.hidden = true;
      try {
        const res = await api<LoginResult>('/auth/login', { login: loginInput.value, password: pwdInput.value, remember: remember.checked, code: codeField.hidden ? undefined : codeInput.value });
        setToken(res.token);
        rememberAccount(res.user);
        cleanup();
        resolve(res);
      } catch (err) {
        const ae = err as ApiError;
        if (ae.code === '2fa_required' || ae.code === '2fa_failed') {
          // druhý faktor: ukázat pole pro kód, heslo nechat vyplněné
          codeField.hidden = false;
          flash.textContent = ae.code === '2fa_failed' ? tk(ae.message) : '';
          flash.hidden = ae.code !== '2fa_failed';
          codeInput.value = '';
          codeInput.focus();
          return;
        }
        flash.textContent = tk(ae.message ?? 'core.login.failed');
        flash.hidden = false;
        pwdInput.value = '';
        pwdInput.focus();
      } finally {
        submit.disabled = false;
        submit.textContent = t('core.ui.sign_in');
      }
    });

    const canvas = h('canvas', { class: 'wind', 'aria-hidden': 'true' });
    const art = h(
      'div',
      { class: 'login-art' },
      canvas,
      logoUrl ? h('img', { class: 'brand-logo', src: logoUrl, alt: brandName }) : h('div', { class: 'wlogo' }, svg(IWAN_MARK), h('span', null, boot.system.name)),
      h('div', { class: 'art-text' }, h('div', { class: 'big' }, brandName), h('p', { class: 'art-sub' }, tagline)),
      h('div', { class: 'art-foot' }, h('span', null, `${boot.system.name} ${boot.system.version}`), ' · ', h('a', { href: boot.system.vendor.url, target: '_blank', rel: 'noopener' }, boot.system.vendor.name)),
      svg(IWAN_MARK, 'deco'),
    );
    const box = h(
      'div',
      { class: 'login-form' },
      h(
        'div',
        { class: 'box' },
        h('div', { class: 'mlogo' }, svg(IWAN_MARK), h('span', null, boot.system.name)),
        h('h1', null, options.switching ? t('desktop.shell.switch_user') : t('core.ui.sign_in')),
        h('p', { class: 'lead muted' }, brandName),
        tiles,
        form,
        h('div', { class: 'powered' }, h('a', { href: boot.system.vendor.url, target: '_blank', rel: 'noopener', class: 'powered-logo', 'aria-label': boot.system.vendor.name }, svg(WANET_LOGO)), h('span', null, t('core.ui.powered', { name: boot.system.name, version: boot.system.version }))),
      ),
    );
    const wrap = h('div', { class: 'login-wrap' }, art, box);
    container.replaceChildren(wrap);
    const stopWind = startWind(canvas);
    const cleanup = () => {
      stopWind();
      wrap.remove();
    };
    (tiles ? null : loginInput.value ? pwdInput : loginInput)?.focus();
  });
}
